import React, { useEffect, useState } from "react"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem
} from "@material-ui/core"
import Grid from "@material-ui/core/Grid"
import { FormProvider, useForm } from "react-hook-form"
import { Toast } from "components/shared/Toaster"
import "./CompleteProfile.scss"
import TextFieldForm from "components/forms/TextFieldForm"
import { statesOptions } from "constants/constantVariables"
import strings from "strings"
import Auth from "tools/AuthService"
import { validatePassword, phoneNumberValidation } from "tools/forms/validators"

const t = strings.LOGIN_STRING

// Drops empty/blank fields so the PATCH only sends what the user actually filled
// in (the backend requires only firstName/lastName/companyName).
const compact = (obj) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      acc[key] = String(value).trim()
    }
    return acc
  }, {})

/**
 * Dismissable, non-blocking "complete your profile" reminder. Shown after login
 * when /v1/user/me returns status === "Pending". Collects User + Client fields
 * in three sections. Only firstName / lastName / companyName are required.
 *
 * ⚠️ There are two distinct phone fields: `phone` (the user's personal phone)
 * and `companyPhone` (the client's phone). They must not be mixed.
 *
 * On submit, Auth.completeRegistration replaces the stored tokens with the fresh
 * authToken/refreshToken from the response — mandatory, since setting a password
 * bumps the tokenVersion and invalidates the current token.
 */
const CompleteProfileModal = ({ open, user, onDismiss, onCompleted }) => {
  const methods = useForm()
  const { handleSubmit, reset } = methods
  const [loading, setLoading] = useState(false)

  // Prefill the temporary placeholders (name/company created for the lead) so
  // the user can correct them, rather than starting from blank.
  useEffect(() => {
    if (!open) return
    reset({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: "",
      companyName: user?.securityContext?.client?.clientName || user?.companyName || "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      companyPhone: "",
      ein: "",
      website: "",
      password: ""
    })
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (data) => {
    setLoading(true)
    const body = compact({
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      companyName: data.companyName,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      state: data.state,
      zip: data.zip,
      companyPhone: data.companyPhone,
      ein: data.ein,
      website: data.website,
      password: data.password
    })
    Auth.completeRegistration(body)
      .then(() => {
        Toast({ type: "success", message: t.COMPLETE_SUCCESS })
        onCompleted && onCompleted()
      })
      .catch(async (err) => {
        // 400 → a required field is missing/invalid; surface the backend message.
        let message = t.COMPLETE_ERROR
        try {
          const parsed = await err?.json?.()
          if (Array.isArray(parsed) && parsed[0]?.message) message = parsed[0].message
          else if (parsed?.message) message = parsed.message
        } catch { /* keep generic message */ }
        Toast({ type: strings.ERROR, message })
        setLoading(false)
      })
  }

  return (
    <Dialog open={!!open} onClose={onDismiss} maxWidth="md" fullWidth>
      <FormProvider {...methods}>
        <form name="complete-profile" onSubmit={handleSubmit(submit)}>
          <DialogTitle>{t.COMPLETE_TITLE}</DialogTitle>
          <DialogContent dividers>
            <div className="msg" style={{ marginBottom: 8 }}>{t.COMPLETE_HINT}</div>

            <Grid container spacing={2} alignItems="flex-start" className="completeProfileGrid">
              <Grid item xs={12} className="completeProfileSection completeProfileSection--first">
                <h3 className="completeProfileSectionTitle">{t.COMPLETE_SECTION_PERSONAL}</h3>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="firstName" name="firstName" label={t.COMPLETE_FIRST_NAME} variant="outlined" size="small" required disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="lastName" name="lastName" label={t.COMPLETE_LAST_NAME} variant="outlined" size="small" required disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="phone" name="phone" label={t.COMPLETE_PHONE} optional variant="outlined" size="small" validate={phoneNumberValidation} disabled={loading} />
              </Grid>

              <Grid item xs={12} className="completeProfileSection">
                <h3 className="completeProfileSectionTitle">{t.COMPLETE_SECTION_COMPANY}</h3>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="companyName" name="companyName" label={t.COMPLETE_COMPANY} variant="outlined" size="small" required disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="website" name="website" label={t.COMPLETE_WEBSITE} optional variant="outlined" size="small" disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="address1" name="address1" label={t.COMPLETE_ADDRESS1} optional variant="outlined" size="small" disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="address2" name="address2" label={t.COMPLETE_ADDRESS2} optional variant="outlined" size="small" disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextFieldForm fullWidth margin="none" id="city" name="city" label={t.COMPLETE_CITY} optional variant="outlined" size="small" disabled={loading} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextFieldForm select fullWidth margin="none" id="state" name="state" label={t.COMPLETE_STATE} optional variant="outlined" size="small" disabled={loading}>
                  <MenuItem value="">—</MenuItem>
                  {statesOptions["USA"].map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextFieldForm>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextFieldForm fullWidth margin="none" id="zip" name="zip" label={t.COMPLETE_ZIP} optional variant="outlined" size="small" disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="companyPhone" name="companyPhone" label={t.COMPLETE_COMPANY_PHONE} optional variant="outlined" size="small" validate={phoneNumberValidation} disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="ein" name="ein" label={t.COMPLETE_EIN} optional variant="outlined" size="small" disabled={loading} />
              </Grid>

              <Grid item xs={12} className="completeProfileSection">
                <h3 className="completeProfileSectionTitle">{t.COMPLETE_SECTION_SECURITY}</h3>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldForm fullWidth margin="none" id="password" name="password" label={t.COMPLETE_PASSWORD} type="password" showPasswordIcon variant="outlined" size="small" validate={validatePassword} disabled={loading} />
              </Grid>
              <Grid item xs={12}>
                <div className="msg">{t.COMPLETE_PASSWORD_HINT}</div>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onDismiss} color="primary" disabled={loading}>
              {t.COMPLETE_LATER}
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={22} color="inherit" /> : t.COMPLETE_NOW}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export default CompleteProfileModal
